/* eslint-disable no-param-reassign */
const chartConfigCompressor = (chartConfigOptions) => {
  const commons = {
    filters: 'f',
    statement: 'se',
    percentage: 'pe',
    gridLines: 'gl',
    cardType: 'cy',
    chartOptions: 'cs',
    chartType: 'ct',
    charts: 'ch',
    color: 'co',
    visibleLegends: 'v',
    name: 'n',
    icon: 'i',
    enabled: 'e',
    animationDuration: 'ad',
    grid: 'g',
    label: 'l',
    left: 'le',
    right: 'r',
    bottom: 'b',
    show: 'sh',
    position: 'p',
    trigger: 'tg',
    width: 'wi',
    legend: 'lg',
    selected: 'sc',
    pageIconColor: 'pi',
    xAxis: 'xx',
    yAxis: 'yy',
    boundaryGap: 'bg',
    series: 'ss',
    containLabel: 'cl',
    alignWithLabel: 'aw',
    lineStyle: 'ls',
    orient: 'o',
    radius: 'rs',
    avoidLabelOverlap: 'a',
    labelLine: 'll',
    minShowLabelAngle: 'm',
    normal: 'nl',
    emphasis: 'ep',
    fontWeight: 'fw',
    interval: 'il',
    lineHeight: 'lh',
    smooth: 'st',
    symbolSize: 'sy',
    dataset: 'd',
    source: 'sr',
    seriesLayoutBy: 'sl',
    stack: 's',
    textStyle: 'ts',
    fontSize: 'fs',
    type: 't',
    data: 'dt',
    value: 'vl',
    numeric: 'nm',
    size: 'sz',
    height: 'h',
    weight: 'w',
    enunciado: 'en',
    pageTextStyle: 'ps',
    axisTick: 'at',
    axisLabel: 'al',
    axisLine: 'ai',
    subject: 'sb',
  };
  // Patterns contém os padrões de conversão que devem
  // ocorrer para cada value do objeto
  const patterns = {
    // Verifica se o parâmetro passado se encaixa com um
    // dos padrões definidos
    compressValueByPattern(object) {
      if (Array.isArray(object)) {
        if (object.filter((item) => item === null).length > 1) return patterns.NxN;
      } else if (typeof object === 'object') {
        if (object && Object.keys(object).toString() === 'vl,nm,n,l') {
          return patterns.dtSO;
        }

        if (object && Object.keys(object).toString() === 'sb,vl,nm') {
          return patterns.sbnO;
        }

        if (object && Object.keys(object).toString() === 'n,i,vl,nm') {
          return patterns.nivO;
        }
      } else if (typeof object === 'boolean') {
        return object ? patterns.True : patterns.False;
      } else if (typeof object === 'string') {
        if (object === 'circle') {
          return patterns.circle;
        } if (object === 'stack') {
          return patterns.stack;
        }
      }

      return null;
    },
    // Em séries de data, alguns indices de um array podem vir como
    // { value, numeric, name, label }, que são propriedades estáticas
    // então podemos mapear apenas para retornar os valores
    dtSO(object) {
      return `dtSO${Object.values(object)}`;
    },
    // Em data, alguns indices de um array podem vir no formato
    // { subject, value, numeric } que será mapeado para svnO{v1,v2,v3...,vN}
    sbnO(object) {
      return `sbnO${Object.values(object)}`;
    },
    // Em data alguns indices de um array podem vir no formato
    // { name, icon, value, numeric } que será mapeado para nivO{v1, v2, v3... vN}
    nivO(object) {
      return `nivO${Object.values(object)}`;
    },
    // Padrão de conversão de nulos simultâneos no pattern
    // NxN, ou seja [1,3, null, null, null] -> [1,3,Nx3]
    NxN(object) {
      const nullIntervals = [];

      object.forEach((item, itemIndex) => {
        const nextIsNull = object[itemIndex + 1] === null;
        if (item === null && nextIsNull && !nullIntervals.length) {
          nullIntervals.push(itemIndex);
        } else if (item === null && nullIntervals.length === 1 && !nextIsNull) {
          nullIntervals.push(itemIndex);
        }

        if (nullIntervals.length === 2) {
          const [startNullIndex, endNullIndex] = nullIntervals;
          object[startNullIndex] = `Nx${(endNullIndex + 1) - startNullIndex}`;

          for (let j = startNullIndex + 1; j <= endNullIndex; j += 1) {
            delete object[j];
          }

          nullIntervals.length = 0;
        }
      });

      return object.filter(String);
    },
    True() {
      return 'T';
    },
    False() {
      return 'F';
    },
    // Caso a propriedade possua o valor "circle" será convertido
    // para 'C'.
    circle() {
      return 'C';
    },
    stack() {
      return commons.stack;
    },
  };

  // Realiza o cálculo de compressão do nome dos atributos para cada propriedade
  const recursive = (object) => {
    // Verifica se o objeto passado por parâmetro é realmente um objeto ou um array
    if (object !== null && typeof object === 'object') {
      // Calcula as keys mais externas do objeto passado
      const mostExternalKeys = Object.keys(object);
      // Itera cada propriedade
      mostExternalKeys.forEach((externalKey) => {
        const externalKeyValue = object[externalKey];
        // Calcula o similar de acordo com a propriedade iterada do objeto original
        const similarKeyValue = commons[externalKey];
        // Verifica se existe um valor similar
        if (similarKeyValue) {
          // Adiciona o valor de referência da key iterada à sua propriedade similar
          object[similarKeyValue] = externalKeyValue;
          // Deleta o valor de referência atrelado à key antiga
          delete object[externalKey];
        }

        // Itera novamente de forma recursiva o objeto mais externo
        recursive(externalKeyValue);
      });
    }
  };

  // Realiza o cálculo de compressão dos valores para cada propriedade
  const recursivePatterns = (object) => {
    // Verifica se o objeto passado por parâmetro é realmente um objeto ou um array
    if (object !== null && typeof object === 'object') {
      // Calcula as keys mais externas do objeto passado
      const mostExternalKeys = Object.keys(object);
      // Itera cada propriedade
      mostExternalKeys.forEach((externalKey) => {
        const externalKeyValue = object[externalKey];
        const valueCompressionStrategy = patterns.compressValueByPattern(externalKeyValue);

        if (valueCompressionStrategy) {
          object[externalKey] = valueCompressionStrategy(externalKeyValue);
        }

        // Itera novamente de forma recursiva o objeto mais externo
        recursivePatterns(object[externalKey]);
      });
    }
  };

  recursive(chartConfigOptions);
  recursivePatterns(chartConfigOptions);

  return chartConfigOptions;
};

export default chartConfigCompressor;
